import React from "react";
import { withTranslation } from "react-i18next";
import styled from "styled-components";

const FooterWrapper = styled.footer`
  background-color: #2E186A;
  padding: 70px 0;
  margin-top: 50px;
`;

const Container = styled.div`
  max-width: 1170px;
  margin: auto;
  padding: 0 20px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const FooterCol = styled.div`
  width: 25%;
  padding: 0 15px;
  
  @media(max-width: 767px) {
    width: 50%;
    margin-bottom: 30px;
  }
  
  @media(max-width: 574px) {
    width: 100%;
  }
`;

const FooterHeading = styled.h4`
  font-size: 18px;
  color: #ffffff;
  text-transform: capitalize;
  margin-bottom: 35px;
  font-weight: 500;
  position: relative;
  font-family: 'Motiva Sans Bold', serif;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: -10px;
    background-color: #DA726A;
    height: 2px;
    box-sizing: border-box;
    width: 50px;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FooterListItem = styled.li`
  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const FooterLink = styled.a`
  font-size: 16px;
  text-transform: capitalize;
  color: #bbbbbb;
  text-decoration: none;
  font-weight: 300;
  display: block;
  transition: all 0.3s ease;
  font-family: 'Motiva Sans Light', sans-serif;
  
  &:hover {
    color: #ffffff;
    padding-left: 8px;
  }
`;

const EmailLink = styled.a`
  font-size: 16px;
  text-transform: capitalize;
  color: #DA726A;
  text-decoration: underline;
  font-weight: 500;
  display: inline-block;
  transition: all 0.3s ease;
  font-family: 'Motiva Sans Bold', serif;
  padding: 8px 16px;
  border-radius: 6px;
  background-color: rgba(218, 114, 106, 0.1);
  border: 1px solid rgba(218, 114, 106, 0.3);
  
  &:hover {
    color: #ffffff;
    background-color: #DA726A;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(218, 114, 106, 0.3);
    text-decoration: none;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MapLink = styled.a`
  font-size: 16px;
  text-transform: capitalize;
  color: #DA726A;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-family: 'Motiva Sans Bold', serif;
  padding: 8px 16px;
  border-radius: 6px;
  background-color: rgba(218, 114, 106, 0.1);
  border: 1px solid rgba(218, 114, 106, 0.3);
  
  &:before {
    content: "📍";
    font-size: 14px;
  }
  
  &:hover {
    color: #ffffff;
    background-color: #DA726A;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(218, 114, 106, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FooterText = styled.p`
  font-size: 16px;
  color: #bbbbbb;
  text-decoration: none;
  font-weight: 300;
  display: block;
  margin-bottom: 10px;
  line-height: 1.6;
  font-family: 'Motiva Sans Light', sans-serif;
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  background-color: rgba(255,255,255,0.2);
  border-radius: 50%;
  transition: all 0.5s ease;
  text-decoration: none;
  
  &:hover {
    background-color: #ffffff;
    transform: translateY(-2px);
  }
  
  img {
    width: 20px;
    height: 20px;
    filter: none;
    transition: all 0.5s ease;
  }
  
  &:hover img {
    filter: brightness(0) invert(1);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 30px;
  margin-top: 40px;
  border-top: 1px solid rgba(255,255,255,0.1);
  color: #bbbbbb;
  font-family: 'Motiva Sans Light', sans-serif;
`;

interface FooterProps {
  t: (key: string) => string;
}

const NewFooter = ({ t }: FooterProps) => {
  return (
    <FooterWrapper>
      <Container>
        <Row>
          <FooterCol>
            <FooterHeading>Pilatrópico</FooterHeading>
            <FooterList>
              <FooterListItem>
                <FooterText>Feel connected to your body through the method of Pilates.</FooterText>
              </FooterListItem>
            </FooterList>
          </FooterCol>
          
          <FooterCol>
            <FooterHeading>{t("Contact")}</FooterHeading>
            <FooterList>
              <FooterListItem>
                <FooterText>{t("Do you have any question? Feel free to reach out.")}</FooterText>
              </FooterListItem>
              <FooterListItem>
                <EmailLink href="mailto:pilatropico.pr@gmail.com">
                  {t("Email us!")}
                </EmailLink>
              </FooterListItem>
            </FooterList>
          </FooterCol>
          
          <FooterCol>
            <FooterHeading>{t("Address")}</FooterHeading>
            <FooterList>
              <FooterListItem>
                <FooterText>PR 2 Km 117.6</FooterText>
              </FooterListItem>
              <FooterListItem>
                <FooterText>Bo. Ceiba Baja</FooterText>
              </FooterListItem>
              <FooterListItem>
                <FooterText>Aguadilla, PR 00605</FooterText>
              </FooterListItem>
              <FooterListItem>
                <MapLink 
                  href="https://maps.app.goo.gl/cq752y8iuoDfgmN47"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Location
                </MapLink>
              </FooterListItem>
            </FooterList>
          </FooterCol>
          
          <FooterCol>
            <FooterHeading>{t("Social Media")}</FooterHeading>
            <SocialLinks>
              <SocialLink 
                href="https://www.facebook.com/pilatropico.pr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/img/svg/facebookColor.svg" alt="Facebook" />
              </SocialLink>
              <SocialLink 
                href="https://www.instagram.com/pilatropico.pr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/img/svg/instagramColor.svg" alt="Instagram" />
              </SocialLink>
            </SocialLinks>
          </FooterCol>
        </Row>
        
        <Copyright>
          <p>© 2024 Pilatrópico. All rights reserved.</p>
        </Copyright>
      </Container>
    </FooterWrapper>
  );
};

export default withTranslation()(NewFooter); 